import { vec3 } from 'gl-matrix'
import { GLContext } from 'nanogl/types'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import Node from 'nanogl-node'
import { Uniform } from 'nanogl-pbr/Input'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { HexToNewVec3 } from '@/webgl/core/Color'
import { RenderContext } from '@/webgl/core/RenderContext'
import DebugDraw from '@/webgl/dev/debugDraw/DebugDraw'
import gui from '@/webgl/dev/gui'
import AssetDatabase from '@/webgl/resources/AssetDatabase'
import GltfResource from '@/webgl/resources/GltfResource'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import ScrollingTexture from '@/webgl/runner/rendering/chunks/ScrollingTexture'
import { backgroundCutout } from '@/webgl/runner/rendering/RenderingUtils'

const LANE_PATH = 'common/lanes.glb'

export type LaneTextureId = 'wood' | 'book' | 'rule' | 'ticket'

export default class LanesRenderer implements ILoadable {
  world: GameWorld
  gl: GLContext
  gltf: Gltf
  node: Node

  scroll: ScrollingTexture
  time: number = 0
  color: Uniform
  maskColor: Uniform

  textureId: LaneTextureId = 'wood'

  constructor(gl: GLContext, world: GameWorld) {
    this.world = world
    this.gl = gl
    this.node = new Node()
    this.color = new Uniform('uBaseColorFactor', 3)
    this.maskColor = new Uniform('uMaskColor', 3)

    this.world.root.add(this.node)

    this.scroll = new ScrollingTexture(this.gl)

    const fld = gui.folder('Lanes')
    fld.select('Texture', ['wood', 'book', 'rule', 'ticket']).onChange((v: string) => {
      this.textureId = v as LaneTextureId
      this.loadCurrentTexture()
    })
  }

  public async load(abort: AbortSignal): Promise<void> {
    await this.loadCurrentTexture()

    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()

    overrides.overridePass('lane', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      backgroundCutout(this.gl, pass)
      // pass.glconfig.depthMask(false);
      pass.inputs.add(this.scroll)
      pass.baseColorFactor.attach(this.color)
      return passInstance.pass
    })

    overrides.overridePass('lane_mask', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      backgroundCutout(this.gl, pass)
      pass.glconfig.depthMask(false)
      pass.baseColorFactor.attach(this.maskColor)
      return passInstance.pass
    })

    this.gltf = await new GltfResource(LANE_PATH, this.gl, { extensions: [overrides] }).load()

    this.node.add(this.gltf.root)
  }

  private async loadCurrentTexture() {
    const graduation = await AssetDatabase.getTexture(`common/${this.textureId}.jpg`, this.gl, {
      alpha: true,
      aniso: 16,
      miplinear: true,
    }).load()
    this.scroll.setMainTex(graduation)
  }

  public setTextureId(id: LaneTextureId) {
    this.textureId = id
  }

  public setColor(color: number) {
    this.color.set(...HexToNewVec3(color))
  }

  public setMaskColor(color: number) {
    this.maskColor.set(...HexToNewVec3(color))
  }

  public preRender(time: TimeEntity) {
    const settings = this.world.settings
    this.time -= time.unscaledDeltaTime * settings.scrollSpeed
    this.scroll.timeUniform.set(this.time)
  }

  public render(context: RenderContext) {
    for (const renderable of this.gltf.renderables) {
      renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    }

    DebugDraw.drawGuizmo(vec3.fromValues(0, 0, 0))
  }
}
